import React, { useState, useMemo } from 'react';
import { branding } from '../../constants/branding';

interface TraceStep {
  step: number;
  address: string;
  amount: number;
  riskScore: number;
  timestamp: string;
  isHighRisk?: boolean;
  isSanctioned?: boolean;
  isExchange?: boolean;
  exchangeName?: string;
  fromAddress?: string;
  toAddress?: string;
  transactionHash?: string;
  blockHeight?: number;
  gasUsed?: number;
  gasPrice?: number;
  fee?: number;
}

interface KYTTraceStepsProps {
  traceSteps: TraceStep[];
  rootAddress: string;
  loading?: boolean;
}

type ViewMode = 'timeline' | 'table' | 'flow';
type SortField = 'step' | 'amount' | 'riskScore' | 'timestamp';
type SortDirection = 'asc' | 'desc';

interface FilterState {
  riskLevel: string;
  stepRange: { min: number; max: number };
  showOnlySanctioned: boolean;
  showOnlyExchanges: boolean;
  showOnlyHighRisk: boolean;
  searchTerm: string;
}

const KYTTraceSteps: React.FC<KYTTraceStepsProps> = ({
  traceSteps,
  rootAddress,
  loading = false
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [sortField, setSortField] = useState<SortField>('step');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<FilterState>({
    riskLevel: 'all',
    stepRange: { min: 0, max: 10 },
    showOnlySanctioned: false,
    showOnlyExchanges: false,
    showOnlyHighRisk: false,
    searchTerm: ''
  });

  // Utility functions
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 8
    }).format(amount);
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRiskLevelColor = (riskScore: number) => {
    if (riskScore >= 80) return '#9C27B0'; // Critical
    if (riskScore >= 60) return '#F44336'; // High
    if (riskScore >= 30) return '#FF9800'; // Medium
    return '#4CAF50'; // Low
  };

  const getRiskLevel = (riskScore: number) => {
    if (riskScore >= 80) return 'CRITICAL';
    if (riskScore >= 60) return 'HIGH';
    if (riskScore >= 30) return 'MEDIUM';
    return 'LOW';
  };

  const getRiskBadgeStyle = (riskScore: number) => {
    const color = getRiskLevelColor(riskScore);
    return {
      backgroundColor: color,
      color: 'white',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '10px',
      fontWeight: '600',
      textTransform: 'uppercase' as const
    };
  };

  // Filter and sort trace steps
  const filteredAndSortedSteps = useMemo(() => {
    let filtered = traceSteps.filter(step => {
      // Risk level filter
      if (filters.riskLevel !== 'all') {
        const stepRiskLevel = getRiskLevel(step.riskScore);
        if (stepRiskLevel !== filters.riskLevel) return false;
      }

      // Step range filter
      if (step.step < filters.stepRange.min || step.step > filters.stepRange.max) {
        return false;
      }

      // Sanctioned filter
      if (filters.showOnlySanctioned && !step.isSanctioned) {
        return false;
      }

      // Exchange filter
      if (filters.showOnlyExchanges && !step.isExchange) {
        return false;
      }

      // High risk filter
      if (filters.showOnlyHighRisk && !step.isHighRisk) {
        return false;
      }

      // Search term filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const matchesSearch = 
          step.address.toLowerCase().includes(searchLower) ||
          (step.exchangeName && step.exchangeName.toLowerCase().includes(searchLower)) ||
          (step.transactionHash && step.transactionHash.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }

      return true;
    });

    // Sort steps
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortField) {
        case 'step':
          aValue = a.step;
          bValue = b.step;
          break;
        case 'amount':
          aValue = Math.abs(a.amount);
          bValue = Math.abs(b.amount);
          break;
        case 'riskScore':
          aValue = a.riskScore;
          bValue = b.riskScore;
          break;
        case 'timestamp':
          aValue = new Date(a.timestamp).getTime();
          bValue = new Date(b.timestamp).getTime();
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [traceSteps, filters, sortField, sortDirection]);

  const toggleStepExpansion = (stepId: string) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  const updateFilter = (updates: Partial<FilterState>) => {
    setFilters(prev => ({ ...prev, ...updates }));
  };

  // Generate unique ID for each step
  const getStepId = (step: TraceStep) => {
    return `${step.step}-${step.address}-${step.timestamp}`;
  };

  // Statistics
  const stats = useMemo(() => {
    const stats = {
      total: traceSteps.length,
      low: 0,
      medium: 0,
      high: 0,
      critical: 0,
      sanctioned: 0,
      exchanges: 0,
      totalVolume: 0
    };

    traceSteps.forEach(step => {
      stats.totalVolume += Math.abs(step.amount);
      if (step.isSanctioned) stats.sanctioned++;
      if (step.isExchange) stats.exchanges++;
      
      const riskLevel = getRiskLevel(step.riskScore);
      switch (riskLevel) {
        case 'LOW': stats.low++; break;
        case 'MEDIUM': stats.medium++; break;
        case 'HIGH': stats.high++; break;
        case 'CRITICAL': stats.critical++; break;
      }
    });

    return stats;
  }, [traceSteps]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        padding: '40px',
        backgroundColor: 'white',
        borderRadius: 12,
        border: `1px solid ${branding.borderColor}`
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40,
            height: 40,
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#666', margin: 0 }}>Loading trace steps...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      backgroundColor: 'white', 
      borderRadius: 12, 
      border: `1px solid ${branding.borderColor}`,
      padding: 24,
      marginTop: 24
    }}>
      {/* Header with View Toggle */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 24 
      }}>
        <h3 style={{ margin: 0 }}>Detailed Trace Steps ({traceSteps.length})</h3>
        
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setViewMode('timeline')}
            style={{
              padding: '8px 16px',
              backgroundColor: viewMode === 'timeline' ? branding.primaryColor : 'transparent',
              color: viewMode === 'timeline' ? 'white' : branding.textColor,
              border: `1px solid ${branding.primaryColor}`,
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500
            }}
          >
            📅 Timeline
          </button>
          <button
            onClick={() => setViewMode('table')}
            style={{
              padding: '8px 16px',
              backgroundColor: viewMode === 'table' ? branding.primaryColor : 'transparent',
              color: viewMode === 'table' ? 'white' : branding.textColor,
              border: `1px solid ${branding.primaryColor}`,
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500
            }}
          >
            📊 Table
          </button>
          <button
            onClick={() => setViewMode('flow')}
            style={{
              padding: '8px 16px',
              backgroundColor: viewMode === 'flow' ? branding.primaryColor : 'transparent',
              color: viewMode === 'flow' ? 'white' : branding.textColor,
              border: `1px solid ${branding.primaryColor}`,
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500
            }}
          >
            🌊 Flow
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
        gap: 12, 
        marginBottom: 24 
      }}>
        <div style={{ 
          padding: 12, 
          backgroundColor: '#f8f9fa', 
          borderRadius: 8, 
          textAlign: 'center',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Total Steps</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: branding.primaryColor }}>
            {stats.total}
          </div>
        </div>
        <div style={{ 
          padding: 12, 
          backgroundColor: '#e8f5e8', 
          borderRadius: 8, 
          textAlign: 'center',
          border: '1px solid #4CAF50'
        }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Low Risk</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#4CAF50' }}>
            {stats.low}
          </div>
        </div>
        <div style={{ 
          padding: 12, 
          backgroundColor: '#fff3e0', 
          borderRadius: 8, 
          textAlign: 'center',
          border: '1px solid #FF9800'
        }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Medium Risk</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#FF9800' }}>
            {stats.medium}
          </div>
        </div>
        <div style={{ 
          padding: 12, 
          backgroundColor: '#ffebee', 
          borderRadius: 8, 
          textAlign: 'center',
          border: '1px solid #F44336'
        }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>High Risk</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#F44336' }}>
            {stats.high}
          </div>
        </div>
        <div style={{ 
          padding: 12, 
          backgroundColor: '#f3e5f5', 
          borderRadius: 8, 
          textAlign: 'center',
          border: '1px solid #9C27B0'
        }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Critical</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#9C27B0' }}>
            {stats.critical}
          </div>
        </div>
        <div style={{ 
          padding: 12, 
          backgroundColor: '#ffebee', 
          borderRadius: 8, 
          textAlign: 'center',
          border: '1px solid #F44336'
        }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Sanctioned</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#F44336' }}>
            {stats.sanctioned}
          </div>
        </div>
        <div style={{ 
          padding: 12, 
          backgroundColor: '#e3f2fd', 
          borderRadius: 8, 
          textAlign: 'center',
          border: '1px solid #2196F3'
        }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Exchanges</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#2196F3' }}>
            {stats.exchanges}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: 16, 
        marginBottom: 24,
        padding: 20,
        backgroundColor: '#f8f9fa',
        borderRadius: 8,
        border: '1px solid #e9ecef'
      }}>
        <div>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
            Search
          </label>
          <input
            type="text"
            placeholder="Address, exchange, hash..."
            value={filters.searchTerm}
            onChange={(e) => updateFilter({ searchTerm: e.target.value })}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
            Risk Level
          </label>
          <select
            value={filters.riskLevel}
            onChange={(e) => updateFilter({ riskLevel: e.target.value })}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14
            }}
          >
            <option value="all">All Risk Levels</option>
            <option value="LOW">Low Risk</option>
            <option value="MEDIUM">Medium Risk</option>
            <option value="HIGH">High Risk</option>
            <option value="CRITICAL">Critical Risk</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
            Step Range
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="number"
              placeholder="Min"
              value={filters.stepRange.min}
              onChange={(e) => updateFilter({ 
                stepRange: { ...filters.stepRange, min: Number(e.target.value) }
              })}
              style={{
                width: '50%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14
              }}
            />
            <input
              type="number"
              placeholder="Max"
              value={filters.stepRange.max}
              onChange={(e) => updateFilter({ 
                stepRange: { ...filters.stepRange, max: Number(e.target.value) }
              })}
              style={{
                width: '50%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontSize: 14, fontWeight: 500 }}>
            <input
              type="checkbox"
              checked={filters.showOnlySanctioned}
              onChange={(e) => updateFilter({ showOnlySanctioned: e.target.checked })}
              style={{ marginRight: 8 }}
            />
            Show only sanctioned
          </label>
          <label style={{ fontSize: 14, fontWeight: 500 }}>
            <input
              type="checkbox"
              checked={filters.showOnlyExchanges}
              onChange={(e) => updateFilter({ showOnlyExchanges: e.target.checked })}
              style={{ marginRight: 8 }}
            />
            Show only exchanges
          </label>
          <label style={{ fontSize: 14, fontWeight: 500 }}>
            <input
              type="checkbox"
              checked={filters.showOnlyHighRisk}
              onChange={(e) => updateFilter({ showOnlyHighRisk: e.target.checked })}
              style={{ marginRight: 8 }}
            />
            Show only high risk
          </label>
        </div>
      </div>

      {/* Results Summary */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 16 
      }}>
        <div style={{ fontSize: 14, color: '#666' }}>
          Showing {filteredAndSortedSteps.length} of {traceSteps.length} trace steps
        </div>
      </div>

      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <div style={{ position: 'relative' }}>
          {/* Timeline Line */}
          <div style={{
            position: 'absolute',
            left: 20,
            top: 0,
            bottom: 0,
            width: 2,
            backgroundColor: branding.primaryColor,
            zIndex: 1
          }} />
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {filteredAndSortedSteps.map((step, index) => {
              const stepId = getStepId(step);
              return (
              <div key={stepId} style={{ position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
                {/* Timeline Node */}
                <div style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: getRiskLevelColor(step.riskScore),
                  border: '2px solid white',
                  position: 'absolute',
                  left: 15,
                  top: 8,
                  zIndex: 2,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }} />
                
                {/* Step Content */}
                <div style={{
                  marginLeft: 40,
                  flex: 1,
                  padding: 16,
                  backgroundColor: step.isHighRisk ? '#FFEBEE' : step.isSanctioned ? '#FFEBEE' : '#F8F9FA',
                  border: '1px solid #E0E0E0',
                  borderRadius: 8,
                  position: 'relative'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ 
                          fontSize: 14, 
                          fontWeight: 600, 
                          color: branding.primaryColor 
                        }}>
                          Step {step.step}
                        </span>
                        <span style={getRiskBadgeStyle(step.riskScore)}>
                          {getRiskLevel(step.riskScore)}
                        </span>
                      </div>
                      <div style={{ 
                        fontFamily: 'monospace', 
                        fontSize: 12, 
                        color: '#666',
                        wordBreak: 'break-all'
                      }}>
                        {step.address}
                      </div>
                      {step.exchangeName && (
                        <div style={{ fontSize: 12, color: '#2196F3', marginTop: 4 }}>
                          🏦 {step.exchangeName}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {step.isSanctioned && (
                        <span style={{ 
                          padding: '2px 8px', 
                          backgroundColor: '#F44336', 
                          color: 'white', 
                          borderRadius: 4, 
                          fontSize: 10,
                          fontWeight: 600
                        }}>
                          SANCTIONS
                        </span>
                      )}
                      {step.isHighRisk && (
                        <span style={{ 
                          padding: '2px 8px', 
                          backgroundColor: '#FF9800', 
                          color: 'white', 
                          borderRadius: 4, 
                          fontSize: 10,
                          fontWeight: 600
                        }}>
                          HIGH RISK
                        </span>
                      )}
                      {step.isExchange && (
                        <span style={{ 
                          padding: '2px 8px', 
                          backgroundColor: '#2196F3', 
                          color: 'white', 
                          borderRadius: 4, 
                          fontSize: 10,
                          fontWeight: 600
                        }}>
                          EXCHANGE
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                    Amount: {formatCurrency(step.amount)} | 
                    Risk Score: {step.riskScore}% | 
                    Date: {formatDate(step.timestamp)}
                  </div>
                  
                  <button
                    onClick={() => toggleStepExpansion(stepId)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: 'transparent',
                      border: '1px solid #d1d5db',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 12,
                      color: '#374151'
                    }}
                  >
                    {expandedSteps.has(stepId) ? '▼' : '▶'} Details
                  </button>
                  
                  {/* Expanded Details */}
                  {expandedSteps.has(stepId) && (
                    <div style={{ 
                      marginTop: 12, 
                      padding: 12, 
                      backgroundColor: 'white', 
                      borderRadius: 6,
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                        gap: 12,
                        fontSize: 14
                      }}>
                        {step.fromAddress && (
                          <div>
                            <strong>From Address:</strong>
                            <div style={{ 
                              fontFamily: 'monospace', 
                              fontSize: 12, 
                              color: '#6b7280',
                              wordBreak: 'break-all',
                              marginTop: 4
                            }}>
                              {step.fromAddress}
                            </div>
                          </div>
                        )}
                        {step.toAddress && (
                          <div>
                            <strong>To Address:</strong>
                            <div style={{ 
                              fontFamily: 'monospace', 
                              fontSize: 12, 
                              color: '#6b7280',
                              wordBreak: 'break-all',
                              marginTop: 4
                            }}>
                              {step.toAddress}
                            </div>
                          </div>
                        )}
                        {step.transactionHash && (
                          <div>
                            <strong>Transaction Hash:</strong>
                            <div style={{ 
                              fontFamily: 'monospace', 
                              fontSize: 12, 
                              color: '#6b7280',
                              wordBreak: 'break-all',
                              marginTop: 4
                            }}>
                              {step.transactionHash}
                            </div>
                          </div>
                        )}
                        {step.blockHeight && (
                          <div>
                            <strong>Block Height:</strong>
                            <div style={{ marginTop: 4 }}>{step.blockHeight.toLocaleString()}</div>
                          </div>
                        )}
                        {step.gasUsed && (
                          <div>
                            <strong>Gas Used:</strong>
                            <div style={{ marginTop: 4 }}>{step.gasUsed.toLocaleString()}</div>
                          </div>
                        )}
                        {step.gasPrice && (
                          <div>
                            <strong>Gas Price:</strong>
                            <div style={{ marginTop: 4 }}>{step.gasPrice} Gwei</div>
                          </div>
                        )}
                        {step.fee && (
                          <div>
                            <strong>Transaction Fee:</strong>
                            <div style={{ marginTop: 4 }}>{formatCurrency(step.fee)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div style={{ 
          border: '1px solid #e5e7eb', 
          borderRadius: 8, 
          overflow: 'hidden',
          backgroundColor: 'white'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th 
                  onClick={() => handleSort('step')}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Step {sortField === 'step' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  Address
                </th>
                <th 
                  onClick={() => handleSort('amount')}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Amount {sortField === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  onClick={() => handleSort('riskScore')}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Risk {sortField === 'riskScore' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  Type
                </th>
                <th 
                  onClick={() => handleSort('timestamp')}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Date {sortField === 'timestamp' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedSteps.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ 
                    textAlign: 'center', 
                    padding: '40px', 
                    color: '#6b7280',
                    fontSize: 16
                  }}>
                    No trace steps found matching your criteria
                  </td>
                </tr>
              ) : (
                filteredAndSortedSteps.map((step) => {
                  const stepId = getStepId(step);
                  return (
                  <React.Fragment key={stepId}>
                    <tr style={{ 
                      borderBottom: '1px solid #f3f4f6',
                      backgroundColor: expandedSteps.has(stepId) ? '#f8f9fa' : 'white'
                    }}>
                      <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600 }}>
                        {step.step}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 14 }}>
                        <div style={{ marginBottom: 4 }}>
                          <div style={{ 
                            fontFamily: 'monospace', 
                            fontSize: 12, 
                            color: '#6b7280',
                            wordBreak: 'break-all'
                          }}>
                            {step.address.substring(0, 20)}...
                          </div>
                          {step.exchangeName && (
                            <div style={{ 
                              fontSize: 11, 
                              color: '#2196F3',
                              fontWeight: 500
                            }}>
                              🏦 {step.exchangeName}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ 
                        padding: '12px 16px', 
                        textAlign: 'right',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        color: step.amount >= 0 ? '#059669' : '#dc2626'
                      }}>
                        {formatCurrency(step.amount)}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span style={getRiskBadgeStyle(step.riskScore)}>
                          {getRiskLevel(step.riskScore)}
                        </span>
                        <div style={{ 
                          fontSize: 10, 
                          color: '#666',
                          marginTop: 2
                        }}>
                          {step.riskScore}%
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {step.isSanctioned && (
                            <span style={{ 
                              padding: '1px 6px', 
                              backgroundColor: '#F44336', 
                              color: 'white', 
                              borderRadius: 3, 
                              fontSize: 9,
                              fontWeight: 600
                            }}>
                              SANCTIONS
                            </span>
                          )}
                          {step.isHighRisk && (
                            <span style={{ 
                              padding: '1px 6px', 
                              backgroundColor: '#FF9800', 
                              color: 'white', 
                              borderRadius: 3, 
                              fontSize: 9,
                              fontWeight: 600
                            }}>
                              HIGH RISK
                            </span>
                          )}
                          {step.isExchange && (
                            <span style={{ 
                              padding: '1px 6px', 
                              backgroundColor: '#2196F3', 
                              color: 'white', 
                              borderRadius: 3, 
                              fontSize: 9,
                              fontWeight: 600
                            }}>
                              EXCHANGE
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12 }}>
                        {formatDate(step.timestamp)}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <button
                          onClick={() => toggleStepExpansion(stepId)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: 'transparent',
                            border: '1px solid #d1d5db',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 12,
                            color: '#374151'
                          }}
                        >
                          {expandedSteps.has(stepId) ? '▼' : '▶'} Details
                        </button>
                      </td>
                    </tr>
                    
                    {/* Expanded Row Details */}
                    {expandedSteps.has(stepId) && (
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <td colSpan={7} style={{ padding: '16px' }}>
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                            gap: 16,
                            fontSize: 14
                          }}>
                            {step.fromAddress && (
                              <div>
                                <strong>From Address:</strong>
                                <div style={{ 
                                  fontFamily: 'monospace', 
                                  fontSize: 12, 
                                  color: '#6b7280',
                                  wordBreak: 'break-all',
                                  marginTop: 4
                                }}>
                                  {step.fromAddress}
                                </div>
                              </div>
                            )}
                            {step.toAddress && (
                              <div>
                                <strong>To Address:</strong>
                                <div style={{ 
                                  fontFamily: 'monospace', 
                                  fontSize: 12, 
                                  color: '#6b7280',
                                  wordBreak: 'break-all',
                                  marginTop: 4
                                }}>
                                  {step.toAddress}
                                </div>
                              </div>
                            )}
                            {step.transactionHash && (
                              <div>
                                <strong>Transaction Hash:</strong>
                                <div style={{ 
                                  fontFamily: 'monospace', 
                                  fontSize: 12, 
                                  color: '#6b7280',
                                  wordBreak: 'break-all',
                                  marginTop: 4
                                }}>
                                  {step.transactionHash}
                                </div>
                              </div>
                            )}
                            {step.blockHeight && (
                              <div>
                                <strong>Block Height:</strong>
                                <div style={{ marginTop: 4 }}>{step.blockHeight.toLocaleString()}</div>
                              </div>
                            )}
                            {step.gasUsed && (
                              <div>
                                <strong>Gas Used:</strong>
                                <div style={{ marginTop: 4 }}>{step.gasUsed.toLocaleString()}</div>
                              </div>
                            )}
                            {step.gasPrice && (
                              <div>
                                <strong>Gas Price:</strong>
                                <div style={{ marginTop: 4 }}>{step.gasPrice} Gwei</div>
                              </div>
                            )}
                            {step.fee && (
                              <div>
                                <strong>Transaction Fee:</strong>
                                <div style={{ marginTop: 4 }}>{formatCurrency(step.fee)}</div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Flow View */}
      {viewMode === 'flow' && (
        <div style={{ 
          padding: 20, 
          backgroundColor: '#f8f9fa', 
          borderRadius: 8, 
          textAlign: 'center',
          border: '1px solid #e9ecef'
        }}>
          <h4 style={{ margin: '0 0 12px 0' }}>Flow Visualization</h4>
          <p style={{ fontSize: 14, color: '#666', margin: '0 0 16px 0' }}>
            Visual flow representation showing the path from root address through each step
          </p>
          
          {/* Simple Flow Representation */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 16,
            marginTop: 20
          }}>
            {/* Root Address */}
            <div style={{
              padding: '12px 16px',
              backgroundColor: branding.primaryColor,
              color: 'white',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              textAlign: 'center',
              minWidth: 120
            }}>
              <div>ROOT</div>
              <div style={{ fontSize: 10, opacity: 0.8, marginTop: 4 }}>
                {rootAddress.substring(0, 8)}...
              </div>
            </div>
            
            {/* Flow Steps */}
            {filteredAndSortedSteps.slice(0, 5).map((step, index) => (
              <React.Fragment key={step.step}>
                {/* Arrow */}
                <div style={{ 
                  fontSize: 20, 
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  →
                </div>
                
                {/* Step Node */}
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: getRiskLevelColor(step.riskScore),
                  color: 'white',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  textAlign: 'center',
                  minWidth: 120,
                  position: 'relative'
                }}>
                  <div>Step {step.step}</div>
                  <div style={{ fontSize: 10, opacity: 0.8, marginTop: 4 }}>
                    {step.address.substring(0, 8)}...
                  </div>
                  <div style={{ fontSize: 10, opacity: 0.8, marginTop: 2 }}>
                    {formatCurrency(step.amount)}
                  </div>
                  
                  {/* Risk Badges */}
                  <div style={{ 
                    position: 'absolute', 
                    top: -8, 
                    right: -8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                  }}>
                    {step.isSanctioned && (
                      <span style={{ 
                        padding: '1px 4px', 
                        backgroundColor: '#F44336', 
                        color: 'white', 
                        borderRadius: 3, 
                        fontSize: 8,
                        fontWeight: 600
                      }}>
                        🚨
                      </span>
                    )}
                    {step.isExchange && (
                      <span style={{ 
                        padding: '1px 4px', 
                        backgroundColor: '#2196F3', 
                        color: 'white', 
                        borderRadius: 3, 
                        fontSize: 8,
                        fontWeight: 600
                      }}>
                        🏦
                      </span>
                    )}
                  </div>
                </div>
              </React.Fragment>
            ))}
            
            {/* More Steps Indicator */}
            {filteredAndSortedSteps.length > 5 && (
              <>
                <div style={{ 
                  fontSize: 20, 
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  →
                </div>
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#e9ecef',
                  color: '#666',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  textAlign: 'center',
                  minWidth: 120
                }}>
                  <div>+{filteredAndSortedSteps.length - 5}</div>
                  <div style={{ fontSize: 10, marginTop: 4 }}>
                    more steps
                  </div>
                </div>
              </>
            )}
          </div>
          
          <div style={{ 
            marginTop: 20, 
            fontSize: 12, 
            color: '#666',
            display: 'flex',
            justifyContent: 'center',
            gap: 20
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 8, height: 8, backgroundColor: '#4CAF50', borderRadius: '50%' }}></div>
              <span>Low Risk</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 8, height: 8, backgroundColor: '#FF9800', borderRadius: '50%' }}></div>
              <span>Medium Risk</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 8, height: 8, backgroundColor: '#F44336', borderRadius: '50%' }}></div>
              <span>High Risk</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 8, height: 8, backgroundColor: '#9C27B0', borderRadius: '50%' }}></div>
              <span>Critical Risk</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KYTTraceSteps;
